import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { bucketlistService } from "api/services/bucketlist.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Select } from "components/UI/Input";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";
import { getApiErrorMessage } from "utils/api-error";
import { BUCKETLIST_ACHIEVED_OPTIONS, labelForCategory } from "utils/constant/bucketlist";

import type { BucketlistCategory, BucketlistItem } from "api/types/bucketlist.types";

import "pages/AdminPage.scss";
import "./BucketlistCategoryItems.scss";

const PAGE_SIZE = 20;

const formatDateTime = (v: string | null): string => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (v: string | null): string => (v ? String(v).slice(0, 10) : "-");

const muted = (v: unknown): React.ReactNode => (v === null || v === undefined || v === "" ? <span className="bl-items__muted">-</span> : String(v));

const BucketlistCategoryItems: React.FC = () => {
  const navigate = useNavigate();
  const { category } = useParams<{ category: BucketlistCategory }>();
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get("start") ?? "";
  const endDate = searchParams.get("end") ?? "";

  const [items, setItems] = useState<BucketlistItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [achievedFilter, setAchievedFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchItems = useCallback(
    async (targetPage: number, achieved: string) => {
      if (!category) return;
      setLoading(true);
      setError("");
      try {
        const res = await bucketlistService.list({
          category,
          page: targetPage,
          size: PAGE_SIZE,
          ...(achieved ? { is_achieved: achieved === "true" } : {}),
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
        });
        setItems(res.data.results ?? []);
        setTotal(res.data.count ?? 0);
      } catch (err) {
        setError(getApiErrorMessage(err));
        setItems([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [category, startDate, endDate],
  );

  useEffect(() => {
    fetchItems(page, achievedFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, category]);

  const handleAchievedChange = (value: string) => {
    setAchievedFilter(value);
    setPage(1);
    fetchItems(1, value);
  };

  // 작성자 정보가 응답에 포함되어 있는지 (BE 반영 시 자동 노출)
  const hasAuthorInfo = items.some((it) => it.user_id != null || it.user_name != null || it.user_pk != null);

  const columns: Column<BucketlistItem>[] = [
    { key: "user_pk", title: "작성자 PK", width: "90px", render: (v) => muted(v) },
    { key: "user_name", title: "작성자", width: "110px", render: (v) => muted(v) },
    { key: "user_id", title: "작성자 아이디", width: "140px", render: (v) => muted(v) },
    { key: "content", title: "버킷리스트 내용", render: (v) => String(v ?? "-") },
    {
      key: "is_achieved",
      title: "달성",
      width: "72px",
      render: (v) => <Badge variant={v ? "active" : "draft"}>{v ? "달성" : "미달성"}</Badge>,
    },
    { key: "target_date", title: "목표일", width: "110px", render: (v) => formatDate(v) },
    { key: "created_at", title: "작성일시", width: "150px", render: (v) => formatDateTime(v) },
  ];

  const categoryLabel = category ? labelForCategory(category) : "버킷리스트";
  const periodText = startDate || endDate ? `${startDate || "처음"} ~ ${endDate || "오늘"}` : "전체 기간";

  return (
    <div className="admin-page bl-items">
      <ContentHeader title={`${categoryLabel} 버킷리스트`} description={`'${categoryLabel}' 카테고리에 등록된 버킷리스트 항목입니다. (${periodText})`} />

      <div className="bl-items__topbar">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          ← 통계로 돌아가기
        </Button>
      </div>

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <span className="bl-items__count">총 {total.toLocaleString()}건</span>
          <div className="bl-items__filter-right">
            <Select value={achievedFilter} onChange={(e) => handleAchievedChange(e.target.value)} options={BUCKETLIST_ACHIEVED_OPTIONS} style={{ width: 130 }} />
          </div>
        </div>

        {!hasAuthorInfo && <div className="bl-items__notice">작성자·작성자 아이디·작성자 PK는 백엔드 응답에 필드가 추가되면 자동으로 표시됩니다. (별도 BE 요청 진행 중)</div>}

        {error && <div className="admin-page__error">{error}</div>}

        {loading ? <div className="bl-items__loading">불러오는 중...</div> : <Table columns={columns} data={items} rowKey="id" />}

        <Pagination current={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
      </div>
    </div>
  );
};

export default BucketlistCategoryItems;
